import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import { Plus, Edit2, Trash2, Calendar, FileText, X, Save, ChevronLeft, ChevronRight, File, ArrowLeft, Eye } from 'lucide-react';

const ContentForm = ({ content, onSubmit, onCancel, isEdit = false, onContentChange }) => {
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  const addSection = () => {
    const newContent = {
      ...content,
      sections: [...content.sections, { sectionTitle: '', sectionContent: '' }],
    };
    onContentChange(newContent);
  };

  const updateSection = (index, field, value) => {
    const newSections = [...content.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    onContentChange({ ...content, sections: newSections });
  };

  const removeSection = (index) => {
    const newContent = {
      ...content,
      sections: content.sections.filter((_, i) => i !== index),
    };
    onContentChange(newContent);
  };

  const handleTitleChange = (value) => {
    onContentChange({ ...content, title: value });
  };

  const handlePageIdChange = (value) => {
    onContentChange({ ...content, pageId: value });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          {isEdit ? <Edit2 size={18} className="sm:w-5 sm:h-5" /> : <Plus size={18} className="sm:w-5 sm:h-5" />}
          <span className="truncate">{isEdit ? 'Edit Content' : 'Create New Content'}</span>
        </h3>
      </div>
      
      <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">Page ID</label>
          <select
            value={content.pageId}
            onChange={(e) => handlePageIdChange(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm sm:text-base"
            required
            disabled={isEdit}
          >
            <option value="">Select Page ID</option>
            {['about-us', 'copyright', 'faq', 'privacy-policy', 'terms-conditions'].map(id => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">Title</label>
          <input
            type="text"
            ref={titleInputRef}
            value={content.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm sm:text-base"
            placeholder="Enter page title"
            required
          />
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="block text-sm font-semibold text-gray-700">Sections</label>
            <button
              type="button"
              onClick={addSection}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm w-full sm:w-auto"
            >
              <Plus size={16} />
              Add Section
            </button>
          </div>

          {content.sections.map((section, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium text-gray-600">Section {index + 1}</span>
                {content.sections.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSection(index)}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-xs sm:text-sm"
                  >
                    <X size={12} className="sm:w-3.5 sm:h-3.5" />
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Section Title</label>
                  <input
                    type="text"
                    value={section.sectionTitle}
                    onChange={(e) => updateSection(index, 'sectionTitle', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-sm"
                    placeholder="Enter section title (optional)"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Section Content</label>
                  <textarea
                    value={section.sectionContent}
                    onChange={(e) => updateSection(index, 'sectionContent', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="Enter section content (optional)"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              type="submit"
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
            >
              <Save size={16} />
              {isEdit ? 'Update Content' : 'Create Content'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors text-sm sm:text-base w-full sm:w-auto"
            >
              <X size={16} />
              Cancel
            </button>
          </div>

          <div className="hidden sm:flex items-center">
            <button
              type="button"
              onClick={addSection}
              className="flex items-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
            >
              <Plus size={16} />
              Add Section
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const ContentDetail = ({ content, onBack, onEdit, onDelete, isLoading }) => {
  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          Back to Content
        </button>
      </div>

      {/* Content details */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
          <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Eye size={18} className="sm:w-5 sm:h-5" />
            <span className="truncate">{content.title}</span>
          </h2>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Content info */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar size={16} />
                <span>Created: {format(new Date(content.createdAt), 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <File size={16} />
                <span>Page ID: {content.pageId}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => onEdit(content)}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm w-full sm:w-auto disabled:opacity-50"
              >
                <Edit2 size={16} />
                Edit Content
              </button>
              <button
                onClick={() => onDelete(content.pageId)}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm w-full sm:w-auto disabled:opacity-50"
              >
                <Trash2 size={16} />
                Delete Content
              </button>
            </div>
          </div>

          {/* Content sections */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Content Sections</h3>
            {(content.sections || []).length === 0 ? (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No sections added yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {content.sections.map((section, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                        Section {index + 1}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 text-base">
                      {section.sectionTitle || 'No Title'}
                    </h4>
                    <p className="text-gray-500 text-sm leading-relaxed whitespace-pre-wrap">
                      {section.sectionContent || 'No Content'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ContentManagement() {
  const [contents, setContents] = useState({
    contents: [],
    total: 0,
    page: 1,
    limit: 10,
  });
  const [newContent, setNewContent] = useState({ pageId: '', title: '', sections: [{ sectionTitle: '', sectionContent: '' }] });
  const [editContent, setEditContent] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('adminToken');

  const fetchContents = async (page = 1) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        `https://awriter-backend-si7g.onrender.com/api/content?page=${page}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setContents({
        contents: response.data.contents || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
      });
    } catch (error) {
      console.error('Error fetching contents:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to fetch contents');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.post(
        'https://awriter-backend-si7g.onrender.com/api/content',
        newContent,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewContent({ pageId: '', title: '', sections: [{ sectionTitle: '', sectionContent: '' }] });
      setShowCreateForm(false);
      await fetchContents(contents.page);
    } catch (error) {
      console.error('Error creating content:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to create content');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.put(
        `https://awriter-backend-si7g.onrender.com/api/content/${editContent.pageId}`,
        { pageId: editContent.pageId, title: editContent.title, sections: editContent.sections },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditContent(null);
      setSelectedContent(null); // Go back to list after editing
      await fetchContents(contents.page);
    } catch (error) {
      console.error('Error updating content:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to update content');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContent = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) return;
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.delete(
        `https://awriter-backend-si7g.onrender.com/api/content/${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedContent(null); // Go back to list after deleting
      await fetchContents(contents.page);
    } catch (error) {
      console.error('Error deleting content:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to delete content');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (content) => {
    setEditContent({
      pageId: content.pageId,
      title: content.title,
      sections: content.sections || [{ sectionTitle: '', sectionContent: '' }],
    });
  };

  const handleCancelEdit = () => {
    setEditContent(null);
  };

  const handleContentClick = (content) => {
    setSelectedContent(content);
  };

  const handleBackToList = () => {
    setSelectedContent(null);
    setEditContent(null);
  };

  useEffect(() => {
    fetchContents();
    window.scrollTo(0, 0);
  }, []);

  const handlePageChange = (newPage) => {
    fetchContents(newPage);
  };

  // Show content detail view
  if (selectedContent && !editContent) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {errorMessage}
          </div>
        )}
        
        <ContentDetail
          content={selectedContent}
          onBack={handleBackToList}
          onEdit={handleEditClick}
          onDelete={handleDeleteContent}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:py-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Content Management</h1>
        <p className="text-gray-600 text-base sm:text-lg">Manage content for static pages</p>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {errorMessage}
        </div>
      )}

      {/* Add Content Button */}
      {!showCreateForm && !editContent && (
        <div className="text-center">
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors hover:transform hover:scale-105 text-sm sm:text-base w-full sm:w-auto max-w-xs sm:max-w-none mx-auto"
          >
            <Plus size={18} className="sm:w-5 sm:h-5" />
            Add Content
          </button>
        </div>
      )}

      {/* Create Content Form */}
      {showCreateForm && (
        <ContentForm
          content={newContent}
          onSubmit={handleCreateContent}
          onCancel={() => {
            setShowCreateForm(false);
            setNewContent({ pageId: '', title: '', sections: [{ sectionTitle: '', sectionContent: '' }] });
          }}
          onContentChange={setNewContent}
        />
      )}

      {/* Edit Content Form */}
      {editContent && (
        <ContentForm
          content={editContent}
          onSubmit={handleUpdateContent}
          onCancel={handleCancelEdit}
          onContentChange={setEditContent}
          isEdit={true}
        />
      )}

      {/* Content List */}
      {!showCreateForm && !editContent && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 sm:px-6 py-4">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <File size={18} className="sm:w-5 sm:h-5" />
              <span className="truncate">All Content ({contents.total})</span>
            </h2>
          </div>

          <div className="p-4 sm:p-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : contents.contents.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg mb-4">No content found</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors text-sm"
                >
                  <Plus size={16} />
                  Create Your First Content
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {contents.contents.map(content => (
                  <div
                    key={content.pageId}
                    onClick={() => handleContentClick(content)}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 hover:shadow-md transition-all cursor-pointer border border-gray-200 hover:border-gray-300"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{content.title}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-500 mt-1">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} />
                            <span>Created: {format(new Date(content.createdAt), 'MMM dd, yyyy')}</span>
                          </div>
                          <span className="hidden sm:inline text-gray-300">•</span>
                          <div className="flex items-center gap-2">
                            <File size={14} />
                            <span>Page ID: {content.pageId}</span>
                          </div>
                          <span className="hidden sm:inline text-gray-300">•</span>
                          <span>{(content.sections || []).length} section{(content.sections || []).length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Eye size={16} />
                        <span>Click to view</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {contents.total > 0 && contents.contents.length > 0 && (
            <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                Showing {(contents.page - 1) * contents.limit + 1} to{' '}
                {Math.min(contents.page * contents.limit, contents.total)} of{' '}
                {contents.total} contents
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handlePageChange(contents.page - 1)}
                  disabled={contents.page === 1}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex-1 sm:flex-none"
                >
                  <ChevronLeft size={16} />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                <button
                  onClick={() => handlePageChange(contents.page + 1)}
                  disabled={contents.page * contents.limit >= contents.total}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}